import { motion } from 'framer-motion';
import { MessageCircleQuestion } from 'lucide-react';
import type { CogniPrintProfile, DiagnosticDimensionKey } from '@shared/types/cogniprint';
import { getDimensionsFromProfile } from '../dashboard/dimensionHelpers';
import { DimensionMeter, METER_CAPTION } from '../profile/DimensionMeter';

interface ProfileReportCardProps {
  profile: CogniPrintProfile;
  isFree?: boolean;
  /**
   * The dispute hook. When present, every dimension gets a "This doesn't feel
   * right" affordance. A dispute never lets a learner set a number — it asks one
   * more held-back question about that dimension and re-scores it from evidence.
   */
  onDispute?: (dimension: DiagnosticDimensionKey) => void;
  /** Dimensions currently being re-asked, so the row can show it. */
  disputing?: DiagnosticDimensionKey | null;
  /** Dimensions that have already used their one extra question. */
  disputed?: DiagnosticDimensionKey[];
}

export function ProfileReportCard({ profile, isFree = false, onDispute, disputing, disputed = [] }: ProfileReportCardProps) {
  const dimensions = getDimensionsFromProfile(profile, isFree).filter((d) => d.key !== 'priorKnowledge');

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="h-full overflow-y-auto"
    >
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <img src="/ThumbsUpCog.png" alt="Professor Cogni" className="w-16 h-16 mx-auto mb-3 object-contain" />
          <h2 className="font-heading font-bold text-xl">
            Your <span className="text-gold-500">CogniPrint</span>
          </h2>
          {profile.summary?.descriptiveLabel && (
            <p className="text-xs text-indigo-400 font-body font-semibold uppercase tracking-wider mt-2">
              {profile.summary.descriptiveLabel}
            </p>
          )}
        </div>

        {/* Summary */}
        {profile.summary?.summaryText && (
          <div className="rounded-xl bg-dark-subtle border border-dark-border p-4">
            <p className="text-sm text-content-secondary font-body leading-relaxed">
              {profile.summary.summaryText}
            </p>
          </div>
        )}

        {/* Dimensions — each with what it means for THIS learner */}
        <div className="space-y-5">
          <div>
            <h3 className="font-heading font-semibold text-sm text-content-muted">Your Dimensions</h3>
            <p className="text-[11px] text-content-dim font-body mt-0.5">{METER_CAPTION}</p>
          </div>

          {dimensions.map((dim, i) => {
            const isClear = dim.clarity === 'clear';
            const canDispute = !!onDispute && dim.assessed && !dim.locked && !disputed.includes(dim.key as DiagnosticDimensionKey);
            const isDisputing = disputing === dim.key;
            return (
              <motion.div
                key={dim.key}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.06 }}
                className={`rounded-xl border p-3.5 ${isDisputing ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-dark-border/60 bg-dark-surface/40'}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-body font-semibold text-content" style={{ color: dim.color }}>
                    {dim.name}
                  </span>
                  <span className={`text-xs font-body font-semibold ${!dim.assessed ? 'text-content-dim italic' : isClear ? 'text-gold-500' : 'text-content-muted'}`}>
                    {dim.label}
                  </span>
                </div>

                <DimensionMeter dim={dim} size="full" delay={300 + i * 60} />

                {dim.summary && (
                  <p className="text-[13px] text-content-secondary font-body leading-relaxed mt-2.5">
                    {dim.summary}
                  </p>
                )}

                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10.5px] text-content-dim font-body">
                    {dim.assessed ? `Confidence ${Math.round(dim.confidence * 100)}%` : ''}
                  </span>
                  {canDispute && (
                    <button
                      type="button"
                      onClick={() => onDispute(dim.key as DiagnosticDimensionKey)}
                      className="inline-flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 font-body cursor-pointer"
                    >
                      <MessageCircleQuestion size={12} />
                      This doesn't feel right
                    </button>
                  )}
                  {isDisputing && (
                    <span className="text-[11px] text-indigo-400 font-body">Asking one more…</span>
                  )}
                  {!canDispute && !isDisputing && disputed.includes(dim.key as DiagnosticDimensionKey) && (
                    <span className="text-[11px] text-content-dim font-body">Re-scored from your extra answer</span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Confidence */}
        <div className="flex items-center justify-between text-xs text-content-dim font-body pt-2 border-t border-dark-border">
          <span>Overall confidence</span>
          <span>{Math.round(profile.overallConfidence * 100)}%</span>
        </div>

        {/* Upgrade hook */}
        {profile.summary?.upgradeHook && (
          <div className="rounded-lg bg-gold-500/5 border border-gold-500/20 p-3">
            <p className="text-xs text-content-secondary font-body">{profile.summary.upgradeHook}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
